import { logError } from '@ardoq/logging';
import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { readRawValue } from './readRawValue';
import type { EnhancedScopeData } from '@ardoq/data-model';

export const getComponentTypeIdAndParentIdByComponentId = (
  componentId: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const component = enhancedScopeData.componentsById[componentId];
  if (!component) {
    logError(Error('missing component in enhancedScopeData.componentsById'));
    return null;
  }

  return {
    entityId: component.typeId,
    parentEntityId: component.model,
  };
};

export const hasComponentType = (
  componentTypeId: string,
  workspaceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  const modelId = enhancedScopeData.workspacesById[workspaceId]?.componentModel;
  return Boolean(
    enhancedScopeData.typesByModelId[modelId]?.componentTypesById?.[
      componentTypeId
    ]
  );
};

/**
 * @deprecated use enhancedScopeDataOperations.getComponentTypeByComponentId instead
 */
export const getComponentTypeByComponentId = (
  componentId: string,
  enhancedScopeData: EnhancedScopeData
) => {
  return enhancedScopeDataOperations.getComponentTypeByComponentId(
    enhancedScopeData,
    componentId
  );
};

export const getComponentTypeNameByTypeId = (
  typeId: string,
  modelId: string,
  enhancedScopeData: EnhancedScopeData
): string | null =>
  readRawValue(
    APIEntityType.COMPONENT_TYPE,
    typeId,
    'name',
    enhancedScopeData,
    modelId
  ) || null;

export const getComponentTypesByFieldId = (
  fieldId: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const field = enhancedScopeData.fieldsById[fieldId];
  return (
    enhancedScopeData.typesByModelId[field.model]?.componentTypesById ?? {}
  );
};

export const getComponentTypeByTypeIdAndModelId = (
  typeId: string,
  modelId: string,
  { typesByModelId }: EnhancedScopeData
) => typesByModelId[modelId].componentTypesById[typeId];

export const componentTypeCanHaveChildren = (
  typeId: string,
  model: string,
  { modelsById, typesByModelId }: EnhancedScopeData
) =>
  modelsById[model].flexible ||
  Boolean(
    Object.entries(typesByModelId[model].componentTypesById[typeId].children)
      .length
  );

export const getPossibleComponentTypes = (
  componentId: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const component = enhancedScopeData.componentsById[componentId];
  if (!component) {
    logError(Error('missing component in enhancedScopeData.componentsById'));
    return [];
  }

  const model = enhancedScopeData.modelsById[component.model];
  if (!model) {
    logError(Error('missing model in enhancedScopeData.modelsById'));
    return [];
  }

  if (model.flexible) {
    return Object.values(
      enhancedScopeData.typesByModelId[model._id]?.componentTypesById
    );
  }

  if (component.parent) {
    return Object.values(
      getComponentTypeByComponentId(component.parent, enhancedScopeData)
        ?.children ?? {}
    );
  }

  return Object.values(model.root);
};

export const componentTypesHaveParentChildRelationship = (
  parentTypeId: string,
  childTypeId: string,
  model: string,
  { typesByModelId }: EnhancedScopeData
) =>
  Object.prototype.hasOwnProperty.call(
    typesByModelId[model].componentTypesById[parentTypeId].children,
    childTypeId
  );

export const getComponentTypeNameFilter =
  (enhancedScopeData: EnhancedScopeData, typeName: string) =>
  (componentId: ArdoqId) =>
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      enhancedScopeData,
      componentId
    )?.name === typeName;
